import client from './client'
import { fetchAllPages } from './paging'
import { TapEmailMessage, TapEmailTemplate } from '../types/index'

// ---------------------------------------------------------------------------
// Feature #162 — Emails tab API.
//
// Mirrors the snake/camel pattern the rest of the TAP frontend uses
// (see `tapApplications.ts` + `tapResources.ts`). Templates are
// application-agnostic so they live on a top-level `/tap/email-templates`
// collection, while messages are scoped under the application that owns them.
// ---------------------------------------------------------------------------

interface RawEmailMessage {
  id: string
  application_id?: string | null
  thread_id?: string | null
  message_id?: string | null
  direction?: string | null
  from_address?: string | null
  to_addresses?: string | null
  cc_addresses?: string | null
  subject?: string | null
  body_html?: string | null
  body_preview?: string | null
  sent_at?: string | null
  has_attachments?: boolean | null
  status?: string | null
  sent_by_user_id?: string | null
  sent_by_display_name?: string | null
  template_key?: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

const mapEmailMessage = (r: RawEmailMessage): TapEmailMessage => ({
  id: r.id,
  applicationId: r.application_id ?? undefined,
  threadId: r.thread_id ?? undefined,
  messageId: r.message_id ?? undefined,
  direction: r.direction ?? undefined,
  fromAddress: r.from_address ?? undefined,
  toAddresses: r.to_addresses ?? undefined,
  ccAddresses: r.cc_addresses ?? undefined,
  subject: r.subject ?? undefined,
  bodyHtml: r.body_html ?? undefined,
  bodyPreview: r.body_preview ?? undefined,
  sentAt: r.sent_at ?? undefined,
  hasAttachments: r.has_attachments ?? undefined,
  status: r.status ?? undefined,
  sentByUserId: r.sent_by_user_id ?? undefined,
  sentByDisplayName: r.sent_by_display_name ?? undefined,
  templateKey: r.template_key ?? undefined,
  isActive: Boolean(r.is_active),
  createdAt: r.created_at,
  updatedAt: r.updated_at,
})

interface RawEmailTemplate {
  id: string
  key: string
  name: string
  subject: string
  body_html: string
  available_merge_fields?: string[] | null
  is_active: boolean
}

const mapTemplate = (r: RawEmailTemplate): TapEmailTemplate => ({
  id: r.id,
  key: r.key,
  name: r.name,
  subject: r.subject,
  bodyHtml: r.body_html,
  availableMergeFields: r.available_merge_fields ?? undefined,
  isActive: Boolean(r.is_active),
})

export interface ComposePreviewInput {
  templateKey?: string
  subject?: string
  bodyHtml?: string
  toAddresses?: string
  ccAddresses?: string
}

export interface ComposePreview {
  subject: string
  bodyHtml: string
  toAddresses?: string
  templateKey?: string
  // Merge-field context the server used for interpolation — handy for
  // showing the recruiter exactly what was substituted.
  context?: Record<string, string>
}

export interface SendEmailInput {
  subject: string
  bodyHtml: string
  fromAddress?: string
  toAddresses?: string
  ccAddresses?: string
  templateKey?: string
}

// Feature #170: from-address picker options.
export interface FromAddressOption {
  address: string
  label: string
  isShared: boolean
}

// Feature #170: merge-field insertion panel options.
export interface MergeFieldOption {
  key: string
  label: string
  sampleValue: string
}

export const getEmails = async (applicationId: string): Promise<TapEmailMessage[]> => {
  // WI #337: this sent no params, so a busy application's thread was cut off
  // at the server's default 20 messages with nothing to say so. The envelope's
  // `total` was discarded too, so the tab could not even detect it.
  const raw = await fetchAllPages<RawEmailMessage>(`/tap/applications/${applicationId}/emails`)
  return raw.map(mapEmailMessage)
}

export const composeEmail = async (
  applicationId: string,
  input: ComposePreviewInput,
): Promise<ComposePreview> => {
  const payload: Record<string, unknown> = {
    template_key: input.templateKey,
    subject: input.subject,
    body_html: input.bodyHtml,
    to_addresses: input.toAddresses,
    cc_addresses: input.ccAddresses,
  }
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k]
  })
  const response = await client.post<{
    subject: string
    body_html: string
    to_addresses?: string | null
    template_key?: string | null
    context?: Record<string, string> | null
  }>(`/tap/applications/${applicationId}/emails/compose`, payload)
  return {
    subject: response.data.subject,
    bodyHtml: response.data.body_html,
    toAddresses: response.data.to_addresses ?? undefined,
    templateKey: response.data.template_key ?? undefined,
    context: response.data.context ?? undefined,
  }
}

export const sendEmail = async (
  applicationId: string,
  input: SendEmailInput,
): Promise<TapEmailMessage> => {
  const payload: Record<string, unknown> = {
    subject: input.subject,
    body_html: input.bodyHtml,
    from_address: input.fromAddress,
    to_addresses: input.toAddresses,
    cc_addresses: input.ccAddresses,
    template_key: input.templateKey,
  }
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k]
  })
  const response = await client.post<RawEmailMessage>(
    `/tap/applications/${applicationId}/emails/send`,
    payload,
  )
  return mapEmailMessage(response.data)
}

export const getEmailTemplates = async (): Promise<TapEmailTemplate[]> => {
  const response = await client.get<RawEmailTemplate[]>(`/tap/email-templates`)
  return (response.data || []).map(mapTemplate)
}

// Feature #170: available from-addresses for the compose modal.
export const getFromAddresses = async (applicationId: string): Promise<FromAddressOption[]> => {
  const response = await client.get<Array<{ address: string; label: string; is_shared: boolean }>>(
    `/tap/applications/${applicationId}/emails/from-addresses`,
  )
  return (response.data || []).map((r) => ({
    address: r.address,
    label: r.label,
    isShared: r.is_shared,
  }))
}

// Feature #170: merge fields with live sample values for insertion panel.
export const getMergeFields = async (applicationId: string): Promise<MergeFieldOption[]> => {
  const response = await client.get<Array<{ key: string; label: string; sample_value: string }>>(
    `/tap/applications/${applicationId}/emails/merge-fields`,
  )
  return (response.data || []).map((r) => ({
    key: r.key,
    label: r.label,
    sampleValue: r.sample_value,
  }))
}
